import * as styles from "./ParagraphJumpLinks.module.scss";

import { graphql } from "gatsby";
import React, { useEffect, useRef, useState } from "react";
import { Form, FormSelect } from "../../../components/common";
import type { SelectedDropdownItem } from "../../../components/common/FormSelect/FormSelect";
import { Arrow } from "../../../components/common/Icons";
import useWindowWidth from "../../../hooks/useWindowWidth";
import { Colours } from "../../../utils/colours";
import { pushToDataLayer } from "../../../utils/google";
import WrappedSection from "../../WrappedSection/WrappedSection";

const HEADING_CHARACTER_LIMIT = 19;

export interface ParagraphJumpLinksProps {
	title: string;
	text?: string;
	headingType?: string;
}

export const getParagraphJumpLinksProps = ({
	field_title,
	field_jump_links_text,
	field_show_heading_types,
}: Queries.ParagraphJumpLinksDataFragment): ParagraphJumpLinksProps => ({
	title: field_title,
	text: field_jump_links_text,
	headingType: field_show_heading_types,
});
export const ParagraphJumpLinks: React.FC<ParagraphJumpLinksProps> = ({
	title,
	text,
	headingType,
}) => {
	const windowWidth = useWindowWidth();
	const [headingElements, setHeadingElements] = useState<HTMLElement[]>([]);
	const headingRefs = useRef<{ [key: string]: HTMLElement }>({});

	const queryTimeout = useRef<NodeJS.Timeout>(null);

	useEffect(() => {
		const mainContent = document.getElementById("main-content");
		if (mainContent) {
			// Delay the execution by 500 milliseconds (adjust as needed)
			queryTimeout.current = setTimeout(() => {
				// Get all h2 and h3 elements that are not marked to be hidden from jump links
				// if the headingType is set to "both" grab both h2's and h3's
				// if the headingType is set to "h2" grab only h2's
				let headingElements: NodeListOf<HTMLElement>;
				if (headingType === "h2") {
					headingElements = mainContent.querySelectorAll(
						"h2:not([data-variation='hideFromJumpLinks'])",
					);
				} else {
					// Default to selecting both h2 and h3 if headingType is not provided or set to "both"
					headingElements = mainContent.querySelectorAll(
						"h2:not([data-variation='hideFromJumpLinks']), h3:not([data-variation='hideFromJumpLinks'])",
					);
				}

				const elementsArray = Array.from(headingElements) as HTMLElement[];
				setHeadingElements(elementsArray);

				// Populate headingRefs for both scenarios using a for loop
				const refs: { [key: string]: HTMLElement } = {}; // Explicitly define the type
				for (let i = 0; i < elementsArray.length; i++) {
					const heading = elementsArray[i];
					const headingText = heading.textContent || "";
					refs[headingText] = heading;
				}
				headingRefs.current = refs;
			}, 500);
		}

		return () => {
			clearTimeout(queryTimeout.current);
		};
	}, [headingType]);

	const jumpToHeading = (text: string) => {
		const headingElement = headingRefs.current[text];
		if (headingElement) {
			const navHeight = 56; // height of sticky nav
			const boundingRect = headingElement.getBoundingClientRect();
			const scrollTop = window.scrollY || document.documentElement.scrollTop;
			const targetScrollTop = scrollTop + boundingRect.top - navHeight;
			window.scrollTo({ top: targetScrollTop, behavior: "smooth" });
		}
	};

	const handleSelectChange = (
		selectedOption: SelectedDropdownItem | SelectedDropdownItem[] | null,
	) => {
		if (selectedOption && !Array.isArray(selectedOption)) {
			const value = (selectedOption as SelectedDropdownItem).value;
			jumpToHeading(value);
			pushToDataLayer({
				event: "anchor_click" as Google.DataLayerEventName,
				anchor_text: value,
				anchor_url: window.location.href,
			});
		}
	};

	return (
		<WrappedSection sectionName="ParagraphJumpLinks">
			<div className={styles.jumplinkscontainer}>
				<h2 data-variation="hideFromJumpLinks" className={styles.title}>
					{title}
				</h2>
				{text && <p className={styles.text}>{text}</p>}
				{windowWidth >= 900 ? (
					<div className={styles.links}>
						{headingElements.map((heading) => {
							const headingText = heading.textContent || "";
							const jumpLinkOverrideHeading = heading.getAttribute(
								"data-jumplinkstitle",
							);
							let buttonLabel =
								heading.textContent.length > HEADING_CHARACTER_LIMIT
									? `${heading.textContent.slice(
											0,
											HEADING_CHARACTER_LIMIT,
										)}...`
									: heading.textContent;

							if (jumpLinkOverrideHeading) {
								buttonLabel = jumpLinkOverrideHeading;
							}
							return (
								<button
									key={headingText}
									title={headingText}
									aria-label={headingText}
									className={styles.linktag}
									type="button"
									onClick={() => {
										pushToDataLayer({
											event: "anchor_click" as Google.DataLayerEventName,
											anchor_text: headingText,
											anchor_url: window.location.href,
										});
										jumpToHeading(headingText);
									}}
								>
									{buttonLabel}
									<Arrow
										withBorder={false}
										rotation={180}
										fill={Colours.OATMEAL}
										width="18"
										height="18"
									/>
								</button>
							);
						})}
					</div>
				) : (
					<Form
						className={styles.jumpmenuform}
						onSubmitFn={() => {}}
						validationSchema={{}}
					>
						<FormSelect
							className={styles.jumpmenu}
							placeholder="Page topics"
							label="jumpLinksMenu"
							name="jumpLinksMenu"
							options={headingElements.map((heading) => {
								const jumpLinkOverrideHeading = heading.getAttribute(
									"data-jumplinkstitle",
								);
								const label =
									jumpLinkOverrideHeading || heading.textContent || "";
								return {
									label,
									value: heading.textContent || "",
								};
							})}
							onChange={handleSelectChange}
						/>
					</Form>
				)}
			</div>
		</WrappedSection>
	);
};

export default ParagraphJumpLinks;

export const fragment = graphql`
    fragment ParagraphJumpLinksData on paragraph__jump_links {
        __typename
        id
        field_title
        field_jump_links_text
		field_show_heading_types
    }
`;
